/* eslint-disable no-undef */
/* global pendo */
/// <reference types="@types/pendo-io-browser" />

import {Store} from 'redux';

import {isProduction} from '../../libs/shared/helpers/src/environment/envHelpers';
import {ErrorWithData} from '../../libs/shared/helpers/src/types/customErrors';
import {AppState} from '../store/appState';
import {getCompany, getIsCustomer} from '../store/data/company/companySelectors';
import {getIsUserGlobalAdmin, getUser} from '../store/data/teammates/teammatesSelectors';
import {getFirstTeamUserIsAdminOf} from '../store/data/teams/teamsSelectors';
import {getHasNoPendo} from '../store/featureFlags/featureFlagsSelectors';

let isPendoSetup = false;

export function setupPendo(store: Store<AppState>) {
  const state = store.getState();

  if (getHasNoPendo(state)) {
    return;
  }

  const user = getUser(state);
  const company = getCompany(state);
  const isGlobalAdmin = getIsUserGlobalAdmin(state);
  const firstTeamUserIsAdminOf = getFirstTeamUserIsAdminOf(state);
  const isCustomer = getIsCustomer(state);

  if (isPendoSetup) {
    return;
  }

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.text = `
    (function(apiKey){
      (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=[];
      v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
          o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
          y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
          z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
    })('0c64b535-cb29-4d92-45df-299d2803d974');
  `;

  document.head.appendChild(script);

  setTimeout(() => {
    // To prevent id conflicts between production and staging/dev, modify the id accordingly.
    const userId = isProduction() ? String(user.id) : `${String(user.id)}-staging`;
    const companyId = isProduction() ? String(company.id) : `${String(company.id)}-staging`;

    pendo.initialize({
      visitor: {
        id: userId,
        email: user.email,
        isGlobalAdmin,
        isTeamAdmin: Boolean(firstTeamUserIsAdminOf),
        // Necessary for generating dynamic URLs to some settings pages.
        administratingTeamNamespace: firstTeamUserIsAdminOf && firstTeamUserIsAdminOf.namespace,
        creationDate: new Date(user.createdAt).toISOString(),
      },
      account: {
        id: companyId,
        isCustomer,
        creationDate: new Date(company.createdAt).toISOString(),
      },
    });
    isPendoSetup = true;
  }, 100);
}

export enum PendoGuidesEnum {
  CANNED_RESPONSE = '6IzyxiWuWujCteEoQHEpnVZv9qc',
}

interface PendoServiceErrorData {
  guideRequested?: PendoGuidesEnum;
}

export class PendoServiceError extends ErrorWithData<PendoServiceErrorData> {
  constructor(message: string, data: PendoServiceErrorData) {
    super(message, data);
  }
}

export function activatePendoGuide(guideId: PendoGuidesEnum) {
  if (!isPendoSetup) {
    throw new PendoServiceError('Pendo is not set up yet.', {});
  }

  const maybeGuide = pendo.findGuideById(guideId);

  if (!maybeGuide) {
    throw new PendoServiceError(`The guide ${guideId} has not been found in Pendo.`, {
      guideRequested: guideId,
    });
  }

  // Whether or not the guide is actually shown depends on Pendo's targeting
  return Boolean(pendo.showGuideById(guideId));
}
